import { FC, ReactNode, useEffect, useRef } from 'react'
import { Outlet, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import cn from 'classnames'
import { ContractNavigation } from './components/ContractDetailsNavigation'
import styles from './styles.module.scss'
import { ContractDetailsFlyout } from './components/ContractDetailsFlyout'
import { ContractMessagesFlyout } from './components/ContractMessagesFlyout'
import { useFlyoutState } from './hooks/useFlyoutState'
import { FlyoutId } from './flyoutId'
import { Contract } from '@/domain/contract/types/contract'
import { useGetContract } from '@/api/generated/get-contract-controller/get-contract-controller'
import { isApiError } from '@/api/client'
import { Paths } from '@/paths'
import { LoadingPage } from '@/pages/LoadingPage'
import { ErrorState } from '@/components/ErrorState'
import { mapToContract } from '@/domain/contract/services'
import { ContractDetailsHeader } from '@/domain/contract/layouts/ContractDetailsLayout/components/ContractDetailsHeader/ContractDetailsHeader'
import { mapToCustomerMasterData } from '@/domain/contract/services/customerDataMapper'
import { mapToMessages } from '@/domain/contract/services/messagesMapper'

export type ContractPageOutletContext = {
  contract: Contract
}

export const ContractDetailsLayout: FC = () => {
  const { t } = useTranslation()
  const { contractNumber } = useParams<{ contractNumber: string }>()
  const { openFlyoutIds, displayedFlyoutIds, anyFlyoutOpen, toggleFlyout, closeFlyout } = useFlyoutState()

  const { data, isLoading, error } = useGetContract(contractNumber ?? '', {
    query: {
      enabled: !!contractNumber
    }
  })

  const messagesAutoOpened = useRef(false)
  useEffect(() => {
    if (messagesAutoOpened.current || !data) return
    messagesAutoOpened.current = true
    if (mapToMessages(data).messages.length > 0) {
      toggleFlyout(FlyoutId.Messages)
    }
  }, [data, toggleFlyout])

  if (isLoading) {
    return <LoadingPage fullScreen={false} />
  }
  if (error) {
    const status = isApiError(error) ? error.status : undefined

    if (status === 404) {
      return (
        <ErrorState
          title={t('contract.notFound.title')}
          description={t('contract.notFound.description')}
          buttonLabel={t('common.backToSearch')}
          href={Paths.CONTRACTS_SEARCH}
        />
      )
    }

    return <ErrorState title={t('contract.error.title')} description={t('contract.error.description')} buttonLabel={t('common.backToHome')} href={Paths.HOME} />
  }

  const contract = mapToContract(data!)
  const customer = mapToCustomerMasterData(data!)
  const messages = mapToMessages(data!)

  const shrinkMessagesFlyout = displayedFlyoutIds.length > 1 && messages.messages.length === 1

  const renderFlyout = (id: FlyoutId): ReactNode => {
    switch (id) {
      case FlyoutId.Customer:
        return <ContractDetailsFlyout customer={customer} onClose={() => closeFlyout(id)} />
      case FlyoutId.Messages:
        return <ContractMessagesFlyout messages={messages} onClose={() => closeFlyout(id)} />
    }
  }

  return (
    <div className={styles.contractPage}>
      <ContractDetailsHeader value={contract.header} openFlyoutIds={openFlyoutIds} onFlyoutToggle={toggleFlyout} messageCount={messages.messages.length} />
      <div className={styles.contentLayout}>
        <ContractNavigation />
        <div className={styles.contentArea}>
          <div className={styles.contentContainer}>
            <Outlet context={{ contract } satisfies ContractPageOutletContext} />
          </div>
        </div>
        <div className={cn(styles.flyoutWrapper, { [styles.flyoutWrapperOpen]: anyFlyoutOpen })} aria-hidden={!anyFlyoutOpen}>
          <div className={cn(styles.flyoutColumn, { [styles.flyoutColumnOpen]: anyFlyoutOpen })}>
            {displayedFlyoutIds.map((id) => (
              <section key={id} className={id === FlyoutId.Messages && shrinkMessagesFlyout ? styles.flyoutSectionShrink : undefined}>
                {renderFlyout(id)}
              </section>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
